<app-base-analytics-page
  [reportTitle]="'SOC Analytics'"
  (onPdfExport)="onParentPdfExport($event)"
>
  <app-analytics-filter
    [filterConfig]="filterConfig"
    [selectedTimeRange]="selectedTimeRange()"
    [startDate]="startDate()"
    [endDate]="endDate()"
    (onTimeRangeChange)="onTimeRangeChange($event)"
    (onStartDateChange)="onStartDateChange($event)"
    (onEndDateChange)="onEndDateChange($event)"
    (onApplyFilters)="onApplyFilters($event)"
    [filteredProductsByService]="filteredProductsByService()"
    [selectedTools]="selectedTools()"
    (onToolsChange)="onToolsChange($event)"
  ></app-analytics-filter>
  <kendo-pdf-export
    #socPdf
    paperSize="A4"
    [landscape]="true"
    margin="1cm"
    [scale]="0.5"
    forcePageBreak=".page-break, .force-page-break, .soc-page-break"
    [imageResolution]="300"
  >
    <!-- Critical Components -->
    @if (organizationId && startDateString && endDateString) {
      @if (isCriticalDataLoading()) {
        <!-- Distillation Funnel Skeleton -->
        <div class="row g-3 mb-2">
          <div class="col-12">
            <div class="card">
              <div class="card-body">
                <kendo-skeleton
                  shape="rectangle"
                  animation="wave"
                  height="200px"
                ></kendo-skeleton>
              </div>
            </div>
          </div>
        </div>
        <!-- Events to Escalations Bar Chart Skeleton -->
        <div class="row mb-2">
          <div class="col-12">
            <div class="card">
              <div class="card-body">
                <kendo-skeleton
                  shape="rectangle"
                  animation="wave"
                  height="300px"
                ></kendo-skeleton>
              </div>
            </div>
          </div>
        </div>
      } @else {
        <!-- Distillation Funnel -->
        <div class="row g-3 mb-2">
          <div class="col-12">
            <app-distillation-funnel
              [organizationId]="organizationId"
              [startDate]="startDateString"
              [endDate]="endDateString"
              [AlertStatSummary]="alertStatSummary()"
              [ticketTrends]="ticketTrends()"
              [queryType]="QueryType.Filtered"
              [alertVisibleColumns]="socAlertVisibleColumns"
              [ticketVisibleColumns]="socTicketVisibleColumns"
              [securityTool]="getSecurityToolsParameter()"
              [isLoading]="isCriticalDataLoading()"
              [showExcelExport]="false"
              [enableCsvExport]="true"
            ></app-distillation-funnel>
          </div>
        </div>

        <!-- Events to Escalations Bar Chart -->
        <div class="row mb-2">
          <div class="col-12">
            <div class="card h-100">
              <app-events-to-escalations-bar
                [eventsVsEscalationsChartData]="eventsVsEscalationsChartData()"
                [slaAdherenceData]="slaAdherenceData()"
                [organizationId]="organizationId"
                [startDate]="startDateString"
                [endDate]="endDateString"
                [securityTool]="getSecurityToolsParameter()"
                (chartClick)="onEventsToEscalationsChartClick()"
                (openAlertDialog)="onOpenAlertDialogSLA()"
              ></app-events-to-escalations-bar>
            </div>
          </div>
        </div>
      }
    }
    <div class="page-break"></div>

    <!-- Secondary Components -->
    <!-- Disposition Stats -->
    <div class="row mb-2">
      <div class="col-lg-12 col-md-12 col-sm-12">
        @if (isPhase2Loading()) {
          <div class="card h-100">
            <div class="card-header border-0 bg-transparent p-3">
              <div class="d-flex justify-content-between align-items-center">
                <kendo-skeleton
                  shape="text"
                  animation="wave"
                  width="200px"
                  height="24px"
                ></kendo-skeleton>
                <div class="d-flex gap-3">
                  <kendo-skeleton
                    shape="rectangle"
                    animation="wave"
                    width="120px"
                    height="32px"
                  ></kendo-skeleton>
                  <kendo-skeleton
                    shape="circle"
                    animation="wave"
                    width="32px"
                    height="32px"
                  ></kendo-skeleton>
                </div>
              </div>
            </div>
            <div class="card-body p-3">
              <kendo-skeleton
                shape="rectangle"
                animation="wave"
                height="400px"
              ></kendo-skeleton>
            </div>
          </div>
        } @else if (mttdLineGraphData() || mttrLineGraphData()) {
          <div class="card h-100">
            <div class="card-header border-0 bg-transparent p-3">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h5 class="card-title mb-1 d-flex align-items-center gap-2">
                    Disposition Stats
                    <kendo-svg-icon
                      [icon]="infoCircleIcon"
                      size="small"
                      class="info-icon"
                      kendoTooltip
                      title="Mean Time to Disposition (MTTD) illustrates time from alert notification to decision to close or escalate, and Mean Time to Resolve (MTTR) values shows time from ticket creation to closure."
                    ></kendo-svg-icon>
                  </h5>
                  <p class="text-muted small mb-0">
                    MTTD (Mean Time to Disposition) and MTTR (Mean Time to Resolve) performance over time
                  </p>
                </div>
              </div>
            </div>
            <div class="card-body p-3">
              <app-disposition-stats
                [mttdLineGraphData]="mttdLineGraphData()"
                [mttrLineGraphData]="mttrLineGraphData()"
                [mttdStatData]="mttdStatData()"
                [mttrStatData]="mttrStatData()"
                (openAlertDialog)="onOpenAlertDialogMTTD()"
                (openTicketDialog)="onOpenTicketDialogMTTR()"
              ></app-disposition-stats>
            </div>
          </div>
        }
      </div>
    </div>
    <div class="page-break"></div>
    <!-- Top MTTD Detections -->
    <div class="row g-3 mb-3">
      <div class="col-lg col-md-6 col-sm-12">
        @if (isPhase2Loading()) {
          <div class="card h-100">
            <div class="card-header border-0 bg-transparent p-3">
              <div class="d-flex justify-content-between align-items-center">
                <kendo-skeleton
                  shape="text"
                  animation="wave"
                  width="200px"
                  height="24px"
                ></kendo-skeleton>
                <div class="d-flex gap-3">
                  <kendo-skeleton
                    shape="rectangle"
                    animation="wave"
                    width="120px"
                    height="32px"
                  ></kendo-skeleton>
                  <kendo-skeleton
                    shape="circle"
                    animation="wave"
                    width="32px"
                    height="32px"
                  ></kendo-skeleton>
                </div>
              </div>
            </div>
            <div class="card-body p-3">
              <kendo-skeleton
                shape="rectangle"
                animation="wave"
                height="400px"
              ></kendo-skeleton>
            </div>
          </div>
        } @else {
          <app-top-mttd-detections
            [showExcelExport]="false"
            [enableCsvExport]="true"
            [topMttdDetectionsData]="topMttdDetectionsData()"
            [dateRange]="dateRangeArray"
            [organizationId]="organizationId"
            [startDate]="startDateString"
            [endDate]="endDateString"
            [queryType]="QueryType.Filtered"
            [selectedOrgIds]="organizationId ? [organizationId] : []"
            [securityTool]="getSecurityToolsParameter()"
          ></app-top-mttd-detections>
        }
      </div>

      <!-- Ticket Disposition Grid -->
      <div class="col-lg col-md-6 col-sm-12">
        @if (isPhase2Loading()) {
          <div class="card h-100">
            <div class="card-header border-0 bg-transparent p-3">
              <div class="d-flex justify-content-between align-items-center">
                <kendo-skeleton
                  shape="text"
                  animation="wave"
                  width="200px"
                  height="24px"
                ></kendo-skeleton>
                <div class="d-flex gap-3">
                  <kendo-skeleton
                    shape="rectangle"
                    animation="wave"
                    width="120px"
                    height="32px"
                  ></kendo-skeleton>
                  <kendo-skeleton
                    shape="circle"
                    animation="wave"
                    width="32px"
                    height="32px"
                  ></kendo-skeleton>
                </div>
              </div>
            </div>
            <div class="card-body p-3">
              <kendo-skeleton
                shape="rectangle"
                animation="wave"
                height="400px"
              ></kendo-skeleton>
            </div>
          </div>
        } @else {
          <app-ticket-disposition-grid
            [showExcelExport]="false"
            [enableCsvExport]="true"
            [ticketsDispositionData]="ticketsDispositionData()"
            [dateRange]="dateRangeArray"
            [organizationId]="organizationId"
            [startDate]="startDateString"
            [endDate]="endDateString"
            [queryType]="QueryType.Filtered"
            [selectedOrgIds]="organizationId ? [organizationId] : []"
            [securityTool]="getSecurityToolsParameter()"
          ></app-ticket-disposition-grid>
        }
      </div>

      <!-- Customer Disposition Grid -->
      <div class="col-lg col-md-6 col-sm-12">
        @if (isPhase2Loading()) {
          <div class="card h-100">
            <div class="card-header border-0 bg-transparent p-3">
              <div class="d-flex justify-content-between align-items-center">
                <kendo-skeleton
                  shape="text"
                  animation="wave"
                  width="200px"
                  height="24px"
                ></kendo-skeleton>
                <div class="d-flex gap-3">
                  <kendo-skeleton
                    shape="rectangle"
                    animation="wave"
                    width="120px"
                    height="32px"
                  ></kendo-skeleton>
                  <kendo-skeleton
                    shape="circle"
                    animation="wave"
                    width="32px"
                    height="32px"
                  ></kendo-skeleton>
                </div>
              </div>
            </div>
            <div class="card-body p-3">
              <kendo-skeleton
                shape="rectangle"
                animation="wave"
                height="400px"
              ></kendo-skeleton>
            </div>
          </div>
        } @else {
          <app-customer-disposition-grid
            [showExcelExport]="false"
            [enableCsvExport]="true"
            [customerDispositionData]="customerDispositionData()"
            [dateRange]="dateRangeArray"
            [organizationId]="organizationId"
            [startDate]="startDateString"
            [endDate]="endDateString"
            [queryType]="QueryType.Filtered"
            [selectedOrgIds]="organizationId ? [organizationId] : []"
            [securityTool]="getSecurityToolsParameter()"
          ></app-customer-disposition-grid>
        }
      </div>
    </div>
    <div class="page-break"></div>

    <!-- Events vs Escalations Ticket Dialog -->
    @if (showEventsEscalationsDialog()) {
      <app-events-escalations-dialog
        [visible]="showEventsEscalationsDialog()"
        [organizationId]="organizationId!"
        [startDate]="startDateString"
        [endDate]="endDateString"
        [securityTool]="getSecurityToolsParameter()"
        (close)="onEventsEscalationsDialogClose()"
      ></app-events-escalations-dialog>
    }

    <!-- Automated Response Usage Row -->
    <div class="row g-3 mb-3">
      <div class="col-12">
        @if (isPhase3Loading()) {
          <div class="card h-100">
            <div class="card-header border-0 bg-transparent p-3">
              <div class="d-flex justify-content-between align-items-center">
                <kendo-skeleton
                  shape="text"
                  animation="wave"
                  width="200px"
                  height="24px"
                ></kendo-skeleton>
                <div class="d-flex gap-3">
                  <kendo-skeleton
                    shape="rectangle"
                    animation="wave"
                    width="120px"
                    height="32px"
                  ></kendo-skeleton>
                  <kendo-skeleton
                    shape="circle"
                    animation="wave"
                    width="32px"
                    height="32px"
                  ></kendo-skeleton>
                </div>
              </div>
            </div>
            <div class="card-body p-3">
              <kendo-skeleton
                shape="rectangle"
                animation="wave"
                height="400px"
              ></kendo-skeleton>
            </div>
          </div>
        } @else {
          <app-automated-response-usage
            [showExcelExport]="false"
            [enableCsvExport]="true"
            [automatedResponseData]="automatedResponseUsageData()"
            [dateRange]="dateRangeArray"
            [organizationId]="organizationId"
            [startDate]="startDateString"
            [endDate]="endDateString"
            [queryType]="QueryType.Filtered"
            [selectedOrgIds]="organizationId ? [organizationId] : []"
            [securityTool]="getSecurityToolsParameter()"
          ></app-automated-response-usage>
        }
      </div>
    </div>
    <!-- Ticket Closure Outcomes Chart -->
    @defer (when !isPhase3Loading()) {
      <div class="row mb-3">
        <div class="col-12">
          @if (isPhase3Loading()) {
            <div class="card h-100">
              <div class="card-header border-0 bg-transparent p-3">
                <div class="d-flex justify-content-between align-items-center">
                  <kendo-skeleton
                    shape="text"
                    animation="wave"
                    width="200px"
                    height="24px"
                  ></kendo-skeleton>
                  <div class="d-flex gap-3">
                    <kendo-skeleton
                      shape="rectangle"
                      animation="wave"
                      width="120px"
                      height="32px"
                    ></kendo-skeleton>
                    <kendo-skeleton
                      shape="circle"
                      animation="wave"
                      width="32px"
                      height="32px"
                    ></kendo-skeleton>
                  </div>
                </div>
              </div>
              <div class="card-body p-3">
                <kendo-skeleton
                  shape="rectangle"
                  animation="wave"
                  height="400px"
                ></kendo-skeleton>
              </div>
            </div>
          } @else {
            <app-ticket-closure-outcomes
              [showExcelExport]="false"
              [enableCsvExport]="true"
              [ticketClosureOutcomesData]="ticketClosureOutcomesData()"
              [dateRange]="dateRangeArray"
              [organizationId]="organizationId"
              [startDate]="startDateString"
              [endDate]="endDateString"
              [queryType]="QueryType.Filtered"
              [selectedOrgIds]="organizationId ? [organizationId] : []"
              [securityTool]="getSecurityToolsParameter()"
            ></app-ticket-closure-outcomes>
          }
        </div>
      </div>
    }
    <div class="page-break"></div>

    <!-- Top Row - Key Metrics -->
    @defer (when !isPhase3Loading()) {
      <div class="row g-3 mb-3">
        <!-- Ticket Category Performance -->
        <div class="col-lg col-md-6 col-sm-12">
          @if (isPhase3Loading()) {
            <div class="card h-100">
              <div class="card-header border-0 bg-transparent p-3">
                <div class="d-flex justify-content-between align-items-center">
                  <kendo-skeleton
                    shape="text"
                    animation="wave"
                    width="200px"
                    height="24px"
                  ></kendo-skeleton>
                  <div class="d-flex gap-3">
                    <kendo-skeleton
                      shape="rectangle"
                      animation="wave"
                      width="120px"
                      height="32px"
                    ></kendo-skeleton>
                    <kendo-skeleton
                      shape="circle"
                      animation="wave"
                      width="32px"
                      height="32px"
                    ></kendo-skeleton>
                  </div>
                </div>
              </div>
              <div class="card-body p-3">
                <kendo-skeleton
                  shape="rectangle"
                  animation="wave"
                  height="400px"
                ></kendo-skeleton>
              </div>
            </div>
          } @else {
            <app-ticket-category-performance
              [showExcelExport]="false"
              [enableCsvExport]="true"
              [ticketCategoryData]="ticketCategoryPerformanceData()"
              [dateRange]="dateRangeArray"
              [organizationId]="organizationId"
              [startDate]="startDateString"
              [endDate]="endDateString"
              [queryType]="QueryType.Filtered"
              [selectedOrgIds]="organizationId ? [organizationId] : []"
              [securityTool]="getSecurityToolsParameter()"
            ></app-ticket-category-performance>
          }
        </div>

        <!-- ADR vs Manual -->
        <div class="col-lg col-md-6 col-sm-12">
          @if (isPhase3Loading()) {
            <div class="card h-100">
              <div class="card-header border-0 bg-transparent p-3">
                <div class="d-flex justify-content-between align-items-center">
                  <kendo-skeleton
                    shape="text"
                    animation="wave"
                    width="200px"
                    height="24px"
                  ></kendo-skeleton>
                  <div class="d-flex gap-3">
                    <kendo-skeleton
                      shape="rectangle"
                      animation="wave"
                      width="120px"
                      height="32px"
                    ></kendo-skeleton>
                    <kendo-skeleton
                      shape="circle"
                      animation="wave"
                      width="32px"
                      height="32px"
                    ></kendo-skeleton>
                  </div>
                </div>
              </div>
              <div class="card-body p-3">
                <kendo-skeleton
                  shape="rectangle"
                  animation="wave"
                  height="400px"
                ></kendo-skeleton>
              </div>
            </div>
          } @else {
            <app-adr-manual
              [showExcelExport]="false"
              [enableCsvExport]="true"
              [organizationId]="organizationId"
              [adrManualData]="adrManualData()"
              [dateRange]="dateRangeArray"
              [startDate]="startDateString"
              [endDate]="endDateString"
              [queryType]="QueryType.Filtered"
              [selectedOrgIds]="organizationId ? [organizationId] : []"
              [securityTool]="getSecurityToolsParameter()"
            ></app-adr-manual>
          }
        </div>
      </div>
    }

    <!-- Top Detections Row -->
    @defer (when !isPhase4Loading()) {
      <div class="row g-3 mb-3">
        <div class="col-lg-12 col-md-12">
          @if (isPhase4Loading()) {
            <div class="card h-100">
              <div class="card-header border-0 bg-transparent p-3">
                <div class="d-flex justify-content-between align-items-center">
                  <kendo-skeleton
                    shape="text"
                    animation="wave"
                    width="200px"
                    height="24px"
                  ></kendo-skeleton>
                  <div class="d-flex gap-3">
                    <kendo-skeleton
                      shape="rectangle"
                      animation="wave"
                      width="120px"
                      height="32px"
                    ></kendo-skeleton>
                    <kendo-skeleton
                      shape="circle"
                      animation="wave"
                      width="32px"
                      height="32px"
                    ></kendo-skeleton>
                  </div>
                </div>
              </div>
              <div class="card-body p-3">
                <kendo-skeleton
                  shape="rectangle"
                  animation="wave"
                  height="400px"
                ></kendo-skeleton>
              </div>
            </div>
          } @else {
            <app-top-detections
              [showExcelExport]="false"
              [enableCsvExport]="true"
              [organizationId]="organizationId"
              [dateRange]="dateRangeArray"
              [topDetectionsData]="topDetectionsData()"
              (caseFilterChanged)="onTopDetecttionsCaseEscalatedCheckboxChanged($event)"
              [startDate]="startDateString"
              [endDate]="endDateString"
              [queryType]="QueryType.Filtered"
              [selectedOrgIds]="organizationId ? [organizationId] : []"
              [securityTool]="getSecurityToolsParameter()"
            ></app-top-detections>
          }
        </div>
      </div>
    }

    <!-- Suspect Users and Assets Row -->
    @defer (when !isPhase4Loading()) {
      <div class="row g-3 mb-3">
        <!-- Top 5 Suspect Users -->
        <div class="col-lg col-md-6 col-sm-12">
          @if (isPhase4Loading()) {
            <div class="card h-100">
              <div class="card-header border-0 bg-transparent p-3">
                <div class="d-flex justify-content-between align-items-center">
                  <kendo-skeleton
                    shape="text"
                    animation="wave"
                    width="200px"
                    height="24px"
                  ></kendo-skeleton>
                  <div class="d-flex gap-3">
                    <kendo-skeleton
                      shape="rectangle"
                      animation="wave"
                      width="120px"
                      height="32px"
                    ></kendo-skeleton>
                    <kendo-skeleton
                      shape="circle"
                      animation="wave"
                      width="32px"
                      height="32px"
                    ></kendo-skeleton>
                  </div>
                </div>
              </div>
              <div class="card-body p-3">
                <kendo-skeleton
                  shape="rectangle"
                  animation="wave"
                  height="400px"
                ></kendo-skeleton>
              </div>
            </div>
          } @else {
            <app-suspect-users
              [showExcelExport]="false"
              [enableCsvExport]="true"
              [organizationId]="organizationId"
              [dateRange]="dateRangeArray"
              (seriesClick)="onSuspectUsersSeriesClick($event)"
              [topSuspectUsersData]="topSuspectUsersData()"
              [topUserDetailData]="topUserDetailData()"
              [drilldownLoading]="topUserDetailLoading()"
              [startDate]="startDateString"
              [endDate]="endDateString"
              [queryType]="QueryType.Filtered"
              [selectedOrgIds]="organizationId ? [organizationId] : []"
              [securityTool]="getSecurityToolsParameter()"
            ></app-suspect-users>
          }
        </div>

        <!-- Top 5 Suspect Assets -->
        <div class="col-lg col-md-6 col-sm-12">
          @if (isPhase4Loading()) {
            <div class="card h-100">
              <div class="card-header border-0 bg-transparent p-3">
                <div class="d-flex justify-content-between align-items-center">
                  <kendo-skeleton
                    shape="text"
                    animation="wave"
                    width="200px"
                    height="24px"
                  ></kendo-skeleton>
                  <div class="d-flex gap-3">
                    <kendo-skeleton
                      shape="rectangle"
                      animation="wave"
                      width="120px"
                      height="32px"
                    ></kendo-skeleton>
                    <kendo-skeleton
                      shape="circle"
                      animation="wave"
                      width="32px"
                      height="32px"
                    ></kendo-skeleton>
                  </div>
                </div>
              </div>
              <div class="card-body p-3">
                <kendo-skeleton
                  shape="rectangle"
                  animation="wave"
                  height="400px"
                ></kendo-skeleton>
              </div>
            </div>
          } @else {
            <app-suspect-assets
              [showExcelExport]="false"
              [enableCsvExport]="true"
              [organizationId]="organizationId"
              [dateRange]="dateRangeArray"
              (seriesClick)="onSuspectAssetsSeriesClick($event)"
              [topSuspectAssetsData]="topSuspectAssetsData()"
              [topAssetDetailData]="topAssetDetailData()"
              [drilldownLoading]="topAssetDetailLoading()"
              [startDate]="startDateString"
              [endDate]="endDateString"
              [queryType]="QueryType.Filtered"
              [selectedOrgIds]="organizationId ? [organizationId] : []"
              [securityTool]="getSecurityToolsParameter()"
            ></app-suspect-assets>
          }
        </div>
      </div>
    }

    <!-- Detections Disposition Row -->
    <!-- <div class="row g-3 mb-3">
    <div class="col-lg-12 col-md-12">
      <div class="card h-100">
        <div class="card-header d-flex justify-content-between align-items-center border-0 bg-transparent p-3">
          <h5 class="card-title mb-0">
            Detections Disposition
            <span
              kendoTooltip
              tooltipClass="custom-tooltip"
              title="The final outcome status of closed tickets for the selected period."
            >
              <kendo-svg-icon [icon]="infoCircleIcon"></kendo-svg-icon>
            </span>
          </h5>
          <kendo-dropdownbutton
            #detectionDispositionMoreButton
            kendoButton
            [svgIcon]="moreVerticalIcon"
            class="transparent-button"
            [data]="[{ text: 'View Data', name: 'detectiondisposition' }]"
          ></kendo-dropdownbutton>
        </div>
        <div class="card-body p-3">
          <app-disposition-column
            [organizationId]="organizationId"
            [dateRange]="dateRangeArray"
            [dispositionData]="dispositionData"
          ></app-disposition-column>
        </div>
      </div>
    </div>
  </div> -->

    <!-- Unified Geographic Distribution -->
    <div class="row g-3 mb-3">
      <div class="col-12">
        @if (isPhase4Loading()) {
          <div class="card h-100">
            <div class="card-header border-0 bg-transparent p-3">
              <div class="d-flex justify-content-between align-items-center">
                <kendo-skeleton
                  shape="text"
                  animation="wave"
                  width="200px"
                  height="24px"
                ></kendo-skeleton>
                <div class="d-flex gap-3">
                  <kendo-skeleton
                    shape="rectangle"
                    animation="wave"
                    width="120px"
                    height="32px"
                  ></kendo-skeleton>
                  <kendo-skeleton
                    shape="circle"
                    animation="wave"
                    width="32px"
                    height="32px"
                  ></kendo-skeleton>
                </div>
              </div>
            </div>
            <div class="card-body p-3">
              <kendo-skeleton
                shape="rectangle"
                animation="wave"
                height="400px"
              ></kendo-skeleton>
            </div>
          </div>
        } @else if (sourceMapData() || destinationMapData()) {
          <app-unified-geo-map
            [showExcelExport]="false"
            [enableCsvExport]="true"
            [organizationId]="organizationId"
            [dateRange]="dateRangeArray"
            [sourceData]="sourceMapData"
            [destinationData]="destinationMapData"
            [startDate]="startDateString"
            [endDate]="endDateString"
            [queryType]="QueryType.Filtered"
            [selectedOrgIds]="organizationId ? [organizationId] : []"
            [securityTool]="getSecurityToolsParameter()"
          ></app-unified-geo-map>
        }
      </div>
    </div>

    <!-- SLA Adherence Details Dialog - Outside base-analytics-page for full viewport -->
    @if (showSlaPopup()) {
      <kendo-dialog
        (close)="closeSlaPopup()"
        [width]="'95vw'"
        [height]="'90vh'"
        [minWidth]="'800px'"
        [maxWidth]="'1400px'"
      >
        <kendo-dialog-titlebar>SLA Adherence Details</kendo-dialog-titlebar>
        <kendo-grid
          class="mxh-80v"
          [filterable]="true"
          [data]="slaGridService | async"
          [pageable]="{
            buttonCount: 5,
            info: true,
            type: 'numeric',
            pageSizes: [25, 50, 100, 200]
          }"
          [pageSize]="slaGridService.state.take"
          [skip]="slaGridService.state.skip"
          [(filter)]="slaGridService.state.filter"
          [loading]="slaGridService.loading"
          (dataStateChange)="slaGridService.onStateChange($event)"
          [sort]="slaGridService.state.sort"
          [sortable]="true"
          [resizable]="true"
        >
          <ng-template kendoGridToolbarTemplate>
            <button
              type="button"
              kendoGridExcelCommand
              class="cyflare-btn-secondary"
            >
              Export to Excel
            </button>
          </ng-template>
          <kendo-grid-column
            field="customer"
            title="Customer Name"
          ></kendo-grid-column>
          <kendo-grid-column
            field="parent_name"
            title="Parent Account Name"
          ></kendo-grid-column>
          <kendo-grid-column
            field="industry_name"
            title="Industry"
          ></kendo-grid-column>
          <kendo-grid-column
            [filterable]="false"
            field="alert_received_on"
            title="Received On"
          ></kendo-grid-column>
          <kendo-grid-column
            field="case_id"
            title="Case #"
          ></kendo-grid-column>
          <kendo-grid-column
            field="event_name"
            title="Event Name"
          ></kendo-grid-column>
          <kendo-grid-column
            field="case_severity"
            title="Severity"
          ></kendo-grid-column>
          <kendo-grid-column
            [filterable]="false"
            field="case_responded_on"
            title="First Response"
          ></kendo-grid-column>
          <kendo-grid-column
            field="sla_exceeded"
            title="SLA Exceeded"
          ></kendo-grid-column>
          <kendo-grid-column
            field="case_source"
            title="Source"
          ></kendo-grid-column>
          <kendo-grid-excel
            fileName="sla-adherence-data.xlsx"
            [fetchData]="allSlaData"
          ></kendo-grid-excel>
        </kendo-grid>
      </kendo-dialog>
    }
  </kendo-pdf-export>
</app-base-analytics-page>

@if (showAlertDialog()) {
  <app-alert-data-dialog
    [config]="alertDialogConfig()!"
    [visible]="showAlertDialog()"
    (close)="onAlertDialogClose()"
    (exportCsv)="triggerAlertExport()"
  />
}

@if (showTicketDialog()) {
  <app-ticket-data-dialog
    [config]="ticketDialogConfig()!"
    [visible]="showTicketDialog()"
    (close)="onTicketDialogClose()"
    (exportCsv)="triggerTicketExport()"
  />
}
